import { useCallback, useEffect, useRef, useState } from 'react';
import { pickUrduVoice, speakWithOpenAI, speakWithGoogleTTS, stopOpenAITTS, detectLanguage, detectSpeechLang } from '../services/voice';
import { hasOpenAIApiKey } from '../services/openai';

export interface SpeakOptions {
  lang?: 'ur' | 'en';
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (error?: any) => void;
}

export interface UseSpeechSynthesisResult {
  isSpeaking: boolean;
  isSupported: boolean;
  voices: SpeechSynthesisVoice[];
  speak: (text: string, options?: SpeakOptions) => void;
  stop: () => void;
}

const browserSupported =
  typeof window !== 'undefined' &&
  typeof window.speechSynthesis?.getVoices === 'function' &&
  typeof window.speechSynthesis?.speak === 'function';

/**
 * Text-to-speech hook.
 * Prefers OpenAI TTS when an API key is configured and falls back to the
 * browser's native speech synthesis (with an Urdu voice) otherwise.
 */
export function useSpeechSynthesis(): UseSpeechSynthesisResult {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);

  const tokenRef = useRef(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!browserSupported) return undefined;
    const loadVoices = () => setVoices(window.speechSynthesis.getVoices());
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (browserSupported) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const stop = useCallback(() => {
    tokenRef.current += 1;
    stopOpenAITTS(audioRef.current);
    audioRef.current = null;
    if (browserSupported) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
  }, []);

  const speakWithBrowser = useCallback(
    (text: string, token: number, options: SpeakOptions): void => {
      if (!browserSupported) {
        options.onEnd?.();
        return;
      }
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.92;
      utterance.pitch = 1;
      utterance.volume = 1;

      const currentVoices = window.speechSynthesis.getVoices();
      const urduVoice = pickUrduVoice(currentVoices);
      if (urduVoice) {
        utterance.voice = urduVoice;
        utterance.lang = urduVoice.lang;
      } else {
        const lang = options.lang ?? detectLanguage(text);
        utterance.lang = lang === 'ur' ? 'ur-PK' : 'en-US';
      }

      utterance.onstart = () => {
        if (token === tokenRef.current) {
          setIsSpeaking(true);
          options.onStart?.();
        }
      };
      utterance.onend = () => {
        if (token === tokenRef.current) {
          setIsSpeaking(false);
          options.onEnd?.();
        }
      };
      utterance.onerror = () => {
        if (token === tokenRef.current) {
          setIsSpeaking(false);
          options.onError?.();
        }
      };

      window.speechSynthesis.speak(utterance);
    },
    [],
  );

  const speak = useCallback(
    (text: string, options: SpeakOptions = {}) => {
      const trimmed = text.trim();
      if (!trimmed) {
        options.onEnd?.();
        return;
      }

      const token = tokenRef.current + 1;
      tokenRef.current = token;

      // Preferred: OpenAI text-to-speech.
      if (hasOpenAIApiKey()) {
        const onStart = () => {
          if (token === tokenRef.current) {
            setIsSpeaking(true);
            options.onStart?.();
          }
        };

        const onEnd = () => {
          if (token === tokenRef.current) {
            setIsSpeaking(false);
            options.onEnd?.();
          }
        };

        const onError = (error: any) => {
          if (token !== tokenRef.current) return;
          setIsSpeaking(false);
          options.onError?.(error);
        };

        speakWithOpenAI(trimmed, 'nova', onStart, onEnd, onError)
          .then((audio) => {
            if (token !== tokenRef.current || !audio) {
              if (token === tokenRef.current) {
                speakWithBrowser(trimmed, token, options);
              }
              return;
            }

            audioRef.current = audio;
            audio.onended = () => {
              if (token === tokenRef.current) {
                setIsSpeaking(false);
                options.onEnd?.();
              }
            };
            audio.onerror = (event) => {
              if (token === tokenRef.current) {
                setIsSpeaking(false);
                options.onError?.(event);
              }
            };
          })
          .catch(() => {
            if (token !== tokenRef.current) return;
            speakWithBrowser(trimmed, token, options);
          });
        return;
      }

      // Urdu: browser voices often cannot read Urdu, so use Google TTS.
      const speechLang = options.lang ?? detectSpeechLang(trimmed);
      if (speechLang === 'ur') {
        const utterance = speakWithGoogleTTS(trimmed, speechLang, () => {
          if (token === tokenRef.current) {
            setIsSpeaking(true);
            options.onStart?.();
          }
        }, () => {
          if (token === tokenRef.current) {
            setIsSpeaking(false);
            options.onEnd?.();
          }
        }, (error) => {
          if (token === tokenRef.current) {
            setIsSpeaking(false);
            options.onError?.(error);
          }
        });

        if (!utterance) {
          if (token === tokenRef.current) {
            speakWithBrowser(trimmed, token, options);
          }
        }
        return;
      }

      speakWithBrowser(trimmed, token, options);
    },
    [speakWithBrowser],
  );

  return { isSpeaking, isSupported: browserSupported, voices, speak, stop };
}
